import { api } from "./apiSlice";

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

/**
 * Flat shape matching the backend's actual SELECT (see
 * cloudflare_doh_backend/src/services/appointments.ts) — doh_web_frontend's
 * types/appointment.ts modeled nested `donor`/`slot`/`medicalEstablishment`
 * objects that don't match what either endpoint here actually returns.
 */
export interface AppointmentRow {
  id: string;
  appointmentDate: string;
  scheduled: "PENDING" | "COMPLETED" | "CANCELLED";
  donorId: string;
  donorName: string;
  donorNic: string;
  donorBloodGroup: string;
  slotStart?: string | null;
  slotEnd?: string | null;
}

export interface AppointmentDetail extends AppointmentRow {
  donorEmail: string;
  medEstId: string;
  medEstName: string;
  medEstAddress: string;
  slotId: string | null;
  donorDonationBadge: string;
  donorIsActive: boolean;
  donorTotalDonations: number;
  donorTotalPoints: number;
  donorNextEligible: string | null;
  donorProfileImageUrl: string | null;
}

export const appointmentsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAppointmentsByMedicalEstablishment: builder.query<AppointmentRow[], string>({
      query: (medEstId) => `/appointments/medicalEstablishment/${medEstId}`,
      transformResponse: (res: ApiResponse<{ appointments: AppointmentRow[] }>) => res.data.appointments,
      providesTags: ["Appointment"],
    }),
    getAppointmentById: builder.query<AppointmentDetail, string>({
      query: (appointmentId) => `/appointments/${appointmentId}`,
      transformResponse: (res: ApiResponse<AppointmentDetail>) => res.data,
      providesTags: (_r, _e, id) => [{ type: "Appointment", id }],
    }),
    updateAppointmentStatus: builder.mutation<AppointmentDetail, { appointmentId: string; status: "PENDING" | "COMPLETED" | "CANCELLED" }>({
      query: ({ appointmentId, status }) => ({
        url: `/appointments/${appointmentId}/status`,
        method: "POST",
        body: { status },
      }),
      transformResponse: (res: ApiResponse<AppointmentDetail>) => res.data,
      invalidatesTags: ["Appointment"],
    }),
  }),
});

export const { useGetAppointmentsByMedicalEstablishmentQuery, useGetAppointmentByIdQuery, useUpdateAppointmentStatusMutation } = appointmentsApi;
